import { useRef, type FC, type RefObject, type MouseEvent } from 'react';
import { graphql, useFragment } from 'react-relay';
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';
import { SharedFavoriteItem } from '../../components/global/FavoriteIcons/SharedFavoriteItem';
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import {
    BuyerProductTile,
    type ImageBackgroundColor,
} from 'dibs-buyer-product-tile/exports/BuyerProductTile';
import { SRC_SET_SIZES_TILE, VERTICAL_OFFSET_MOBILE_IMG } from '../../constants/imageConstants';
import classnames from 'classnames';
import { useQuickView } from 'dibs-search-product-tile/exports/QuickViewProvider';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import styles from './SharedMobileRelatedItem.scss';

import { type SharedMobileRelatedItem_item$key } from './__generated__/SharedMobileRelatedItem_item.graphql';
import { type SharedMobileRelatedItem_user$key } from './__generated__/SharedMobileRelatedItem_user.graphql';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';

const SMALL_TILE_TYPE = 'imageSmall';
type X_SMALL_TILE_TYPE = 'imageXSmall';

type Props = {
    index: number;
    item: SharedMobileRelatedItem_item$key;
    user: SharedMobileRelatedItem_user$key | null | undefined;
    dataTn?: string;
    customClassName?: string | null;
    favoritesClassName?: string | null;
    favorites: FavoritesProviderChildrenProps;
    currency: string;
    wrapperRef?: RefObject<HTMLDivElement | null>;
    itemsPerRow?: number;
    showLightBox?: boolean;
    imageSize?: X_SMALL_TILE_TYPE | typeof SMALL_TILE_TYPE;
    onClick: (e?: MouseEvent) => void;
    onContentVisible?: () => void;
    onContentLoaded?: () => void;
    hideRecentSoldPrice?: boolean;
    isClient: boolean;
    imageBackgroundColor?: ImageBackgroundColor;
};

export const SharedMobileRelatedItem: FC<Props> = ({
    index,
    item: itemRef,
    user: userRef,
    dataTn,
    customClassName,
    favoritesClassName,
    favorites,
    currency,
    wrapperRef,
    itemsPerRow = 2,
    showLightBox = false,
    imageSize = SMALL_TILE_TYPE,
    onClick,
    onContentVisible,
    onContentLoaded,
    hideRecentSoldPrice,
    isClient,
    imageBackgroundColor,
}) => {
    const item = useFragment(
        graphql`
            fragment SharedMobileRelatedItem_item on Item
            @argumentDefinitions(priceBookName: { type: "String" }) {
                isFurniture
                ...ItemTile_item @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                ...BuyerProductTile_item
                    @arguments(isTrade: $isTrade, priceBookName: $priceBookName, showSeller: false)
                ...FavoritesItem_item
                ...FavoritesItemProvider_item
                ...SharedFavoriteItem_item
            }
        `,
        itemRef
    );
    const user = useFragment(
        graphql`
            fragment SharedMobileRelatedItem_user on User {
                canShowSellerName
                ...FavoritesItemProvider_user
            }
        `,
        userRef
    );

    const tileElementRef = useRef<HTMLDivElement | null>(null);
    const { isItemTileRedesignTestVariant, isContentVisibilitySbVariant } = useServerVarsContext();
    const { canShowSellerName } = user || {};
    const canShowCreatorName = !canShowSellerName && item.isFurniture;
    const { openQuickView, closeQuickView } = useQuickView();

    if (isItemTileRedesignTestVariant) {
        return (
            <FavoritesItemProvider
                item={item}
                user={user || null}
                tileElementRef={wrapperRef || tileElementRef}
            >
                {({ onMouseEnter, onMouseLeave, ...favoritesItemProps }) => (
                    <BuyerProductTile
                        viewer={null}
                        itemSearch={null}
                        item={item}
                        user={null}
                        afterDisplay={isClient}
                        currency={currency}
                        imageBackgroundColor={imageBackgroundColor}
                        index={index}
                        size="small"
                        itemsPerRow={itemsPerRow}
                        hideRecentSoldPrice={hideRecentSoldPrice}
                        onClick={onClick}
                        onContentVisible={onContentVisible}
                        onContentLoaded={onContentLoaded}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        hasQuickView={!!openQuickView && !!closeQuickView}
                        srcSet={{ sizes: SRC_SET_SIZES_TILE }}
                        imageLoadVerticalOffset={VERTICAL_OFFSET_MOBILE_IMG}
                        showMeasurements={false}
                        showMskuSwatch={false}
                        showPrice={isClient}
                        favoritesComponent={
                            <SharedFavoriteItem
                                {...favoritesItemProps}
                                item={item}
                                favorites={favorites}
                                tileSize="small"
                            />
                        }
                        tileElementRef={tileElementRef}
                    />
                )}
            </FavoritesItemProvider>
        );
    }

    return (
        <div
            className={classnames(styles.tileWrapper, customClassName)}
            data-tn={dataTn}
            ref={wrapperRef}
        >
            <FavoritesItemProvider item={item} user={user}>
                {favoritesItemProps => (
                    <ItemTile
                        index={index}
                        item={item}
                        currency={currency}
                        itemsPerRow={itemsPerRow}
                        showSellerName={isClient && canShowSellerName}
                        showCreatorName={canShowCreatorName}
                        onClick={onClick}
                        onContentVisible={onContentVisible}
                        onContentLoaded={onContentLoaded}
                        showQuickView={!!openQuickView && !!closeQuickView}
                        imageSize={imageSize}
                        isMobile
                        useLoFiLazyLoader
                        hideImagePadding
                        disableContentMargin={showLightBox}
                        viewer={null}
                        srcSetSizes={SRC_SET_SIZES_TILE}
                        showMSKUSwatch={false}
                        imageLoadVerticalOffset={VERTICAL_OFFSET_MOBILE_IMG}
                        showMeasurements={false}
                        itemSearch={null}
                        showLightBox={showLightBox}
                        favoritesComponent={
                            <FavoritesItem
                                wrapperClass={classnames(
                                    styles.favoritesWrapper,
                                    favoritesClassName
                                )}
                                heartIconWrapperClass={classnames(styles.heartIconWrapper, {
                                    [styles.showLightBox]: !!showLightBox,
                                })}
                                item={item}
                                {...favorites}
                                {...favoritesItemProps}
                            />
                        }
                        hideRecentSoldPrice={hideRecentSoldPrice}
                        shouldAutoHideContent={isContentVisibilitySbVariant}
                    />
                )}
            </FavoritesItemProvider>
        </div>
    );
};
